import {
  ColumnFilter,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  FormDialogMode,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Delete, Pageview } from '@material-ui/icons'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumCommodity, EnumTipoPeaje } from '../../../api'
import { ProfileModel } from '../../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumNivelDesglose } from '../../../api/Argentia/enums/enumNivelDesglose'
import { EnumTipoPerfil } from '../../../api/oData/enums/enumTipoPerfil'
import { EnumSubtipoPerfil } from '../../../api/oData/enums/enumSubtipoPerfil'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import { enumToSelectOptionArray } from '../../../helpers'
import { getUserPermissions } from '../../../helpers/permissionsHelper'
import { useConfirmationDialog, useHttpError, usePageState } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Pages } from '../../../model/RBAC/constants'
import {
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../redux'
import { mapNewProfileToPostRequest } from '../profiles.mapper'
import { deleteProfileById, saveNewProfile } from '../profiles.services'
import { ProfileFormData, ProfilesFormDialog } from './profilesFormDialog'

interface ProfilesDataTableProps {
  commodity: EnumCommodity
  profileType: EnumTipoPerfil
  profilesData: any[]
  isFetching: boolean
  onProfileDeleted?: () => void
}

export const ProfilesDataTable: React.FC<ProfilesDataTableProps> = ({
  commodity,
  profileType,
  profilesData,
  isFetching,
  onProfileDeleted
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'profiles'])
  const { errorProps } = useHttpError()
  const { dataTableFilter, dataTableSearch } = usePageState()
  const { info, error } = useNotify()
  const history = useHistory()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  const [formDialogOpen, setFormDialogOpen] = useState<boolean>(false)
  const [formMode, setFormMode] = useState<FormDialogMode>(FormDialogMode.ADD)
  const [formInputData, setFormInputData] = useState<ProfileFormData>({} as ProfileFormData)

  // #region Handlers Starts

  // ------------------- DataTable Action Handlers -----------------------------//
  const handleFormAdd = async () => {
    setFormMode(FormDialogMode.ADD)
    setFormDialogOpen(true)
    return true
  }

  const onViewDetailsHandler = async (rowData: ProfileModel) => {
    history.push(NonAuthRoutes.ProfilesEdit, {
      profileData: rowData
    })
    return true
  }

  const onDeleteHandler = async (rowData: ProfileModel) => {
    await getConfirmation(ConfirmationDialogs.DELETE_PROFILE).then(async () => {
      await deleteProfileById(rowData.id!).then(response => {
        if (response.success) {
          info(t('profiles:components.profilesDataTable.notifications.deleteSuccess'))
          onProfileDeleted && onProfileDeleted()
        } else {
          const errMsg =
            response.error?.code === '400'
              ? JSON.parse(response.error.message ?? '')?.error?.message
              : null
          const showServerMsg = errMsg?.startsWith('ERR')
          showServerMsg
            ? info(errMsg.substring(errMsg.indexOf(':') + 1).trim())
            : error(t('profiles:components.profilesDataTable.notifications.deleteError'))
        }
      })
      closeConfirmation()
    })
    return true
  }

  // ------------------- Profiles Form Dialog Handlers -----------------------------//

  const handleFormDialogClose = async (formData: any) => {
    if (formData) {
      if (formMode === FormDialogMode.ADD) {
        const request = mapNewProfileToPostRequest(formData)
        await saveNewProfile(request).then(response => {
          if (response.success) {
            info(t('profiles:forms.add.notifications.success'))
            history.push(NonAuthRoutes.ProfilesEdit, {
              profileData: response.data
            })
          } else {
            error(t('profiles:forms.add.notifications.error'))
            return
          }
        })
      }
    }
    setFormDialogOpen(false)
  }

  // #endregion Handlers Ends

  // #region Functions Starts

  const checkCodeLocally = (code: string, profileID: number | undefined) => {
    return profilesData.some(profile => profile.code.toLowerCase() === code.toLowerCase())
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const profilesDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 1,
    filterType: FilterType.ADVANCED,
    download: true,
    canEdit: false,
    canDelete: false,
    canInsert: true,
    customInsertEditModal: true,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('profiles:components.profilesDataTable.analytics')
    },
    rowsPerPage: 50,
    onRowInsert: handleFormAdd,
    rowCustomActions: [
      {
        icon: <Pageview fontSize="small" />,
        tooltip: t('profiles:components.profilesDataTable.tooltips.viewDetails'),
        onClick: onViewDetailsHandler,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('profiles:components.profilesDataTable.tooltips.delete'),
        onClick: onDeleteHandler,
        userPermission: UserPermission.EDIT
      }
    ],
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  const profilesDataTableColumns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'code',
      label: t('profiles:components.profilesDataTable.columns.code'),
      type: DataType.TEXT,
      columnWidth: 40
    },
    {
      name: 'description',
      label: t('profiles:components.profilesDataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'breakdownLevel',
      label: t('profiles:components.profilesDataTable.columns.breakdownLevel'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumNivelDesglose, 'enums:EnumNivelDesglose'),
      columnWidth: 20
    },
    {
      name: 'toll',
      label: t('profiles:components.profilesDataTable.columns.toll'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoPeaje, 'enums:EnumTipoPeaje'),
      columnWidth: 20
    },
    {
      name: 'profileSubtype',
      label: t('profiles:components.profilesDataTable.columns.profileSubtype'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumSubtipoPerfil, 'enums:EnumSubtipoPerfil'),
      columnWidth: 20
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  if (isFetching) {
    return <DataTableSkeleton rows={5} elevation={1} />
  }

  return (
    <>
      <DataTable
        data={profilesData}
        columns={profilesDataTableColumns}
        options={profilesDataTableOptions}
        info={DataTableErrorHandler(errorProps)}
        userPermissions={getUserPermissions(Pages.Profiles)}
        appliedFilter={dataTableFilter}
        appliedSearch={dataTableSearch}
      />

      <ProfilesFormDialog
        formMode={formMode}
        open={formDialogOpen}
        onClose={handleFormDialogClose}
        inputData={formInputData}
        commodity={commodity}
        profileType={profileType}
        checkCodeLocally={checkCodeLocally}
      />
    </>
  )
}
